import { useState, useEffect } from "react";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const PLATFORMS = ["instagram", "tiktok", "snapchat", "facebook", "twitter", "youtube", "whatsapp"];

export default function ContactSettings() {
  const { refreshSettings } = useApp();
  const [s, setS] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", time: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/blocked-slots").then((r) => setBlocked(r.data));
  }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" />;

  const set = (path, value) => setS((prev) => {
    const copy = structuredClone(prev); const keys = path.split("."); let o = copy;
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
    o[keys[keys.length - 1]] = value; return copy;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { contact: s.contact, socials: s.socials, policies: s.policies, booking_rules: s.booking_rules });
      toast.success("تم الحفظ"); await refreshSettings();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const addBlock = async () => {
    if (!newBlock.date) return;
    const { data } = await api.post("/admin/blocked-slots", newBlock);
    setBlocked([...blocked, data]); setNewBlock({ date: "", time: "" });
  };
  const delBlock = async (id) => { await api.delete(`/admin/blocked-slots/${id}`); setBlocked(blocked.filter((b) => b.id !== id)); };

  const addSocial = () => set("socials", [...(s.socials || []), { platform: "instagram", url: "" }]);
  const updSocial = (i, k, v) => { const arr = [...s.socials]; arr[i][k] = v; set("socials", arr); };
  const delSocial = (i) => set("socials", s.socials.filter((_, idx) => idx !== i));

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">معلومات التواصل</h1>
        <Button onClick={save} disabled={saving} className="rounded-full font-semibold" data-testid="save-contact-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Check className="h-4 w-4 me-1" />} حفظ
        </Button>
      </div>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-3">
        <h2 className="font-bold">بيانات الصالون</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">رقم الهاتف</Label><Input value={s.contact.phone} onChange={(e) => set("contact.phone", e.target.value)} className="rounded-xl h-11" dir="ltr" data-testid="contact-phone" /></div>
          <div><Label className="mb-1 block text-xs">رقم واتساب (مع رمز الدولة)</Label><Input value={s.contact.whatsapp} onChange={(e) => set("contact.whatsapp", e.target.value)} className="rounded-xl h-11" dir="ltr" placeholder="9665XXXXXXXX" data-testid="contact-whatsapp" /></div>
          <div><Label className="mb-1 block text-xs">البريد الإلكتروني</Label><Input value={s.contact.email} onChange={(e) => set("contact.email", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          <div><Label className="mb-1 block text-xs">رابط الموقع (خرائط)</Label><Input value={s.contact.map_url} onChange={(e) => set("contact.map_url", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          <div><Label className="mb-1 block text-xs">الموقع (عربي)</Label><Input value={s.contact.location_ar} onChange={(e) => set("contact.location_ar", e.target.value)} className="rounded-xl h-11" /></div>
          <div><Label className="mb-1 block text-xs">ساعات العمل (عربي)</Label><Input value={s.contact.hours_ar} onChange={(e) => set("contact.hours_ar", e.target.value)} className="rounded-xl h-11" /></div>
        </div>
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-3">
        <div className="flex items-center justify-between"><h2 className="font-bold">حسابات التواصل الاجتماعي</h2><Button size="sm" variant="outline" onClick={addSocial} className="rounded-full border-2" data-testid="add-social-btn"><Plus className="h-4 w-4" /></Button></div>
        {(s.socials || []).map((soc, i) => (
          <div key={i} className="flex gap-2">
            <Select value={soc.platform} onValueChange={(v) => updSocial(i, "platform", v)}>
              <SelectTrigger className="rounded-xl h-11 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={soc.url} onChange={(e) => updSocial(i, "url", e.target.value)} className="rounded-xl h-11 flex-1" dir="ltr" placeholder="https://..." data-testid={`social-url-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => delSocial(i)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-3">
        <h2 className="font-bold">قواعد الحجز</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><Label className="mb-1 block text-xs">وقت الفتح</Label><Input type="time" value={s.booking_rules.open_time} onChange={(e) => set("booking_rules.open_time", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          <div><Label className="mb-1 block text-xs">وقت الإغلاق</Label><Input type="time" value={s.booking_rules.close_time} onChange={(e) => set("booking_rules.close_time", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          <div><Label className="mb-1 block text-xs">مدة الموعد (دقيقة)</Label><Input type="number" min={5} value={s.booking_rules.slot_minutes} onChange={(e) => set("booking_rules.slot_minutes", Math.max(5, +e.target.value || 60))} className="rounded-xl h-11" /></div>
          <div><Label className="mb-1 block text-xs">أقل مهلة حجز (ساعة)</Label><Input type="number" value={s.booking_rules.min_advance_hours} onChange={(e) => set("booking_rules.min_advance_hours", +e.target.value)} className="rounded-xl h-11" /></div>
          <div><Label className="mb-1 block text-xs">أقصى مدى للحجز (يوم)</Label><Input type="number" value={s.booking_rules.max_advance_days} onChange={(e) => set("booking_rules.max_advance_days", +e.target.value)} className="rounded-xl h-11" /></div>
        </div>
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-3">
        <h2 className="font-bold">سياسات الحجز والإلغاء</h2>
        <div><Label className="mb-1 block text-xs">سياسة الحجز (عربي)</Label><Textarea value={s.policies.booking_ar} onChange={(e) => set("policies.booking_ar", e.target.value)} className="rounded-xl" rows={2} /></div>
        <div><Label className="mb-1 block text-xs">سياسة الإلغاء (عربي)</Label><Textarea value={s.policies.cancellation_ar} onChange={(e) => set("policies.cancellation_ar", e.target.value)} className="rounded-xl" rows={2} /></div>
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 space-y-3">
        <h2 className="font-bold">حجب المواعيد</h2>
        <div className="flex gap-2">
          <Input type="date" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} className="rounded-xl h-11" data-testid="block-date" />
          <Input type="time" value={newBlock.time} onChange={(e) => setNewBlock({ ...newBlock, time: e.target.value })} className="rounded-xl h-11 w-32" dir="ltr" placeholder="اليوم كامل" />
          <Button onClick={addBlock} className="rounded-full" data-testid="add-block-btn"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {blocked.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm">
              {b.date} {b.time !== "all" ? `· ${b.time}` : "· اليوم كامل"}
              <button onClick={() => delBlock(b.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
